import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Drop,
  Fire,
  Flask,
  MagnifyingGlass,
  Package,
  Sparkle,
  TestTube,
} from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QuoteDialog from '../components/QuoteDialog';
import Reveal from '../components/Reveal';
import {
  PRODUCT_CATEGORIES,
  PRODUCTS,
  Product,
  ProductCategory,
} from '../data/products';

const categoryIcon = (category: ProductCategory) => {
  switch (category) {
    case 'Cyanide':
      return <Flask size={22} weight="duotone" />;
    case 'Caustic':
      return <Drop size={22} weight="duotone" />;
    case 'Lime':
      return <Sparkle size={22} weight="duotone" />;
    case 'Carbon':
      return <Package size={22} weight="duotone" />;
    case 'Acids':
      return <TestTube size={22} weight="duotone" />;
    case 'Reagents':
      return <Fire size={22} weight="duotone" />;
    default:
      return <Package size={22} weight="duotone" />;
  }
};

const Shop: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const initialCategory = (params.get('category') as ProductCategory | 'All') || 'All';
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'All' | ProductCategory>(
    PRODUCT_CATEGORIES.includes(initialCategory) ? initialCategory : 'All'
  );
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>();

  const filtered = useMemo(() => {
    return PRODUCTS.filter((product) => {
      const matchesCategory = category === 'All' || product.category === category;
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        product.name.toLowerCase().includes(q) ||
        product.description.toLowerCase().includes(q) ||
        product.category.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [category, search]);

  const setFilter = (next: 'All' | ProductCategory) => {
    setCategory(next);
    if (next === 'All') {
      params.delete('category');
    } else {
      params.set('category', next);
    }
    setParams(params, { replace: true });
  };

  const requestQuote = (product?: Product) => {
    setSelectedProduct(product?.name);
    setQuoteOpen(true);
  };

  return (
    <div className="va-page">
      <Header />
      <main className="va-main">
        <div className="va-shop-hero">
          <img src="/images/shop-warehouse.jpg" alt="" />
          <div className="va-shop-hero-content">
            <div className="va-container">
              <p className="va-label" style={{ color: 'var(--va-accent)', marginBottom: '0.65rem' }}>
                Product catalogue
              </p>
              <h1 className="va-h1">Shop mining reagents and site materials</h1>
              <p className="va-lede" style={{ marginTop: '0.85rem' }}>
                Cyanide, caustic, lime, carbon, acids, and assay reagents for mining and construction operations.
              </p>
            </div>
          </div>
        </div>

        <div className="va-container">
          <div className="va-shop-toolbar">
            <label className="va-search" htmlFor="shop-search">
              <MagnifyingGlass size={18} color="#5C6B63" />
              <input
                id="shop-search"
                type="search"
                placeholder="Search products"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <button type="button" className="va-btn va-btn-secondary" onClick={() => requestQuote()}>
              Request a quote
            </button>
          </div>

          <div className="va-filters" role="tablist" aria-label="Product categories">
            {PRODUCT_CATEGORIES.map((item) => (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={category === item}
                className={`va-filter${category === item ? ' is-active' : ''}`}
                onClick={() => setFilter(item)}
              >
                {item}
              </button>
            ))}
          </div>

          <p className="va-body" style={{ margin: '1.25rem 0 1.5rem' }}>
            Showing {filtered.length} product{filtered.length === 1 ? '' : 's'}
            {category !== 'All' ? ` in ${category}` : ''}
          </p>

          <div className="va-grid">
            {filtered.length === 0 ? (
              <div className="va-empty">
                <p className="va-h3" style={{ marginBottom: '0.5rem' }}>No products match</p>
                <p className="va-body">Try another category or clear the search.</p>
              </div>
            ) : (
              filtered.map((product, index) => (
                <Reveal key={product.id} delay={(index % 6) * 40}>
                  <article className="va-card">
                    <div className="va-card-top">
                      <div className="va-card-icon">{categoryIcon(product.category)}</div>
                      <span className="va-badge">{product.category}</span>
                    </div>
                    <div>
                      <h2 className="va-h3">{product.name}</h2>
                      {product.purity ? (
                        <p className="va-body" style={{ marginTop: '0.25rem', color: 'var(--va-accent)', fontWeight: 600 }}>
                          Purity {product.purity}
                        </p>
                      ) : null}
                    </div>
                    <p className="va-body">{product.description}</p>
                    <p className="va-body" style={{ fontSize: '0.9rem' }}>
                      <strong style={{ color: 'var(--va-ink)', fontWeight: 600 }}>Used for:</strong>{' '}
                      {product.applications}
                    </p>
                    <div className="va-card-actions">
                      <button
                        type="button"
                        className="va-btn va-btn-primary"
                        style={{ width: '100%' }}
                        onClick={() => requestQuote(product)}
                      >
                        Request quote
                      </button>
                    </div>
                  </article>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </main>
      <Footer />
      <QuoteDialog
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        productName={selectedProduct}
      />
    </div>
  );
};

export default Shop;
